import { useCollabItemContext } from 'components/CollabItem/CollabItem'
import { Item } from 'components/CollabItem/Item'
import { observer } from 'mobx-react-lite'
import { PropsWithChildren } from 'react'
import { useRootStore } from 'stores/RootStore'
import { Comment, WorkflowReference, WorkflowReferenceType } from 'types'
import { useCommentPermissions } from '../useCommentPermissions'
import Styles from './CommentItem.module.scss'
import cx from 'classnames'
import { CommentItemProps } from './Types'
import { ReactComponent as PinIcon } from 'assets/icons/pin.svg'
import { ReferenceMetadata } from 'components/Comment/ReferenceMetadata'
import { ReactControl } from '../ReactControl/ReactControl'
import { PinControl } from '../PinControl/PinControl'
import { ResolveControl } from '../ResolveControl/ResolveControl'
import { ItemEditForm } from '../ItemEditForm/ItemEditForm'
import dayjs from 'dayjs'
import { useCurrentLayer } from 'hooks/useCurrentLayer'
import { useDocStore } from 'stores/DocStore'
import { DeleteCommentControl } from '../../CleanupMode/DeleteCommentControl'
import { MergeCommentControl } from '../../CleanupMode/MergeCommentControl'
import { computed } from 'mobx'
import { ProseDisplay } from 'common/ProseInput/ProseDisplay'
import { UnreadDot } from 'components/CollabItem/UnreadDot/UnreadDot'
import { CommentOptionsMenu } from 'components/CommentV2/CommentItem/CommentOptionsMenu'

interface ItemComponentProps {
  comment: Comment
  informalFeedbackRequestReference?: WorkflowReference
}

export const ItemHeader = ({
  comment,
  informalFeedbackRequestReference,
  children,
}: PropsWithChildren<ItemComponentProps>) => (
  <Item.Header>
    <Item.Avatar url={comment.user.avatar || undefined} name={comment.user.display_name} />
    <div className={Styles.userAndMeta}>
      <Item.Actor name={comment.user.display_name} actorId={comment.user.id} capitalize />
      {'  '}
      {informalFeedbackRequestReference && <ReferenceMetadata reference={informalFeedbackRequestReference} />}
      <span className={Styles.metaData}>{dayjs(comment.created_at).format('MMM D h:mma')}</span>
    </div>
    {comment.important && <PinIcon className={Styles.pinnedIcon} />}
    {children}
  </Item.Header>
)

export const ItemFooter = observer(({ comment, informalFeedbackRequestReference }: ItemComponentProps) => {
  const docStore = useDocStore()
  const isInlineDetached = computed(() =>
    docStore.inlineDetachedCollabItems.map(({ item }) => item.uuid).includes(comment.uuid),
  ).get()

  return (
    <Item.Footer>
      {comment.replies.length === 0 && (
        <>
          {informalFeedbackRequestReference && 'Review/Approval Feedback'}
          {!informalFeedbackRequestReference && !comment.uuid && 'Document comment'}
          {isInlineDetached && 'Commented text removed'}
        </>
      )}
    </Item.Footer>
  )
})

export const ItemBody = ({ comment }: ItemComponentProps) => {
  return (
    <Item.Body>
      <ProseDisplay html={comment.html_comment} />
    </Item.Body>
  )
}

const CommentItem = observer(({ comment, inCleanupMode = false, hasReplies }: CommentItemProps) => {
  const { canReact, canTogglePin, canToggleResolved, canEdit, canRename, canDelete } = useCommentPermissions(
    comment,
    inCleanupMode,
  )
  const { editing } = useCollabItemContext()
  const rootStore = useRootStore()
  const docStore = useDocStore()
  const currentLayer = useCurrentLayer({ articleKey: docStore.articleId })
  const isEditing = editing?.id === comment.id
  const inLayerCleanupMode = inCleanupMode && currentLayer && !currentLayer.is_main

  const informalFeedbackRequestReference = comment.workflow_references.find(
    ({ workflow_type, metadata }) =>
      workflow_type === WorkflowReferenceType.InformalFeedbackRequest &&
      (metadata.review?.score || metadata.review?.reason),
  )

  const showActionMenu = (canEdit || canRename || canDelete) && !inLayerCleanupMode

  const toggleReaction = (reaction: string) => {
    rootStore.comments.toggleReaction(comment.id, reaction)
  }

  const LayerControls = () => (
    <>
      {/* TODO: Fix this the next time the file is edited. */}
      {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
      {currentLayer!.permissions.can_edit && <DeleteCommentControl comment={comment} />}
      {inLayerCleanupMode && canToggleResolved && <MergeCommentControl comment={comment} />}
    </>
  )

  const MainControls = () => (
    <>
      {canReact && <ReactControl comment={comment} />}
      {canTogglePin && <PinControl comment={comment} />}
      {canToggleResolved && <ResolveControl comment={comment} />}
    </>
  )

  return (
    <Item.Root
      editing={editing?.id === comment.id}
      className={cx(Styles.itemRoot, {
        [Styles.hasReplies]: hasReplies,
        [Styles.isEditing]: isEditing,
      })}
    >
      <UnreadDot isUnread={!comment.read_status} className={Styles.unreadDot} />
      {!isEditing && (
        <Item.ControlGroup>
          {showActionMenu && (
            <Item.Control tooltipTitle="More options">
              <CommentOptionsMenu comment={comment} />
            </Item.Control>
          )}
          {!inLayerCleanupMode && <MainControls />}
          {inLayerCleanupMode && <LayerControls />}
        </Item.ControlGroup>
      )}
      <ItemHeader comment={comment} informalFeedbackRequestReference={informalFeedbackRequestReference} />
      {isEditing ? (
        <ItemEditForm comment={comment} />
      ) : (
        <>
          <ItemBody comment={comment} />
          <ItemFooter comment={comment} informalFeedbackRequestReference={informalFeedbackRequestReference} />
          <Item.Reactions reactions={comment.reactions} onToggleReaction={toggleReaction} />
        </>
      )}
    </Item.Root>
  )
})

export { CommentItem }
